import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { ShoppingCart, Plus, Minus, Clock, MapPin, Phone, Trash2 } from 'lucide-react';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, #d4af37 0%, #b8941f 100%);
  color: white;
  padding: 3rem 0;
  text-align: center;
  border-radius: 0 0 20px 20px;
  margin-bottom: 3rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  font-weight: bold;
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  opacity: 0.9;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MenuSection = styled.section`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
`;

const CartSection = styled.section`
  background: white;
  border-radius: 15px;
  padding: 2rem;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  height: fit-content;
  position: sticky;
  top: 2rem;
`;

const SectionTitle = styled.h2`
  color: #2c3e50;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CategoryTabs = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
`;

const CategoryTab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? '#d4af37' : '#f8f9fa'};
  color: ${props => props.active ? 'white' : '#2c3e50'};
  border: 2px solid #d4af37;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#b8941f' : '#e9ecef'};
  }
`;

const MenuGrid = styled.div`
  display: grid;
  gap: 1rem;
`;

const MenuItem = styled(motion.div)`
  border: 2px solid #e9ecef;
  border-radius: 10px;
  padding: 1rem;
  transition: all 0.3s ease;
  
  &:hover {
    border-color: #d4af37;
    transform: translateY(-2px);
  }
`;

const MenuItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.5rem;
`;

const MenuItemTitle = styled.h3`
  color: #2c3e50;
  font-size: 1.1rem;
  margin: 0;
`;

const MenuItemPrice = styled.span`
  color: #d4af37;
  font-weight: bold;
  font-size: 1.1rem;
`;

const MenuItemDescription = styled.p`
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  line-height: 1.4;
`;

const MenuItemActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const QuantityControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const QuantityButton = styled.button`
  background: #d4af37;
  color: white;
  border: none;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: #b8941f;
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
  }
`;

const Quantity = styled.span`
  font-weight: bold;
  color: #2c3e50;
  min-width: 20px;
  text-align: center;
`;

const CartItems = styled.div`
  margin-bottom: 2rem;
`;

const CartItem = styled(motion.div)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 0;
  border-bottom: 1px solid #e9ecef;
  
  &:last-child {
    border-bottom: none;
  }
`;

const CartItemInfo = styled.div`
  flex: 1;
  
  h4 {
    color: #2c3e50;
    margin: 0 0 0.25rem 0;
    font-size: 1rem;
  }
  
  p {
    color: #7f8c8d;
    margin: 0;
    font-size: 0.9rem;
  }
`;

const CartItemPrice = styled.span`
  color: #d4af37;
  font-weight: bold;
  margin-left: 1rem;
`;

const CartItemActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RemoveButton = styled.button`
  background: #e74c3c;
  color: white;
  border: none;
  width: 25px;
  height: 25px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  &:hover {
    background: #c0392b;
  }
`;

const CartTotal = styled.div`
  border-top: 2px solid #e9ecef;
  padding-top: 1rem;
  margin-bottom: 2rem;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  
  &.total {
    font-weight: bold;
    font-size: 1.2rem;
    color: #2c3e50;
    border-top: 1px solid #e9ecef;
    padding-top: 0.5rem;
  }
`;

const CheckoutButton = styled.button`
  background: #d4af37;
  color: white;
  border: none;
  padding: 1rem;
  border-radius: 8px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  
  &:hover {
    background: #b8941f;
    transform: translateY(-2px);
  }
  
  &:disabled {
    background: #95a5a6;
    cursor: not-allowed;
    transform: none;
  }
`;

const EmptyCart = styled.div`
  text-align: center;
  color: #7f8c8d;
  padding: 2rem;
  
  .icon {
    font-size: 3rem;
    margin-bottom: 1rem;
  }
`;

const RestaurantInfo = styled.div`
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 10px;
  margin-bottom: 2rem;
  
  h4 {
    color: #2c3e50;
    margin-bottom: 0.5rem;
  }
  
  p {
    color: #7f8c8d;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

interface MenuItem {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  image: string;
}

interface CartItem extends MenuItem {
  quantity: number;
}

const menuItems: MenuItem[] = [
  {
    id: 1,
    name: "Poule & Poulette Special",
    description: "Onze signature kipfilet, gemarineerd in speciale kruiden en gegrild tot perfectie.",
    price: 24.50,
    category: "hoofdgerechten",
    image: "🍗"
  },
  {
    id: 2,
    name: "Spicy Poulette Wings",
    description: "Krokante kippenvleugels in onze speciale spicy marinade.",
    price: 16.90,
    category: "voorgerechten",
    image: "🍗"
  },
  {
    id: 3,
    name: "Poulette Burger",
    description: "Gegrilde kipfilet op een vers gebakken broodje met sla, tomaat, ui en speciale saus.",
    price: 18.50,
    category: "burgers",
    image: "🍔"
  },
  {
    id: 4,
    name: "Kip Caesar Salade",
    description: "Verse sla met gegrilde kipfilet, parmezaanse kaas, croutons en caesar dressing.",
    price: 19.90,
    category: "salades",
    image: "🥗"
  },
  {
    id: 5,
    name: "Poulette Curry",
    description: "Milde curry met kipfilet, kokosmelk en verse groenten.",
    price: 22.50,
    category: "hoofdgerechten",
    image: "🍛"
  },
  {
    id: 6,
    name: "Kip Saté",
    description: "Gegrilde kipspiesjes met pindasaus, geserveerd met rijst en groenten.",
    price: 21.90,
    category: "hoofdgerechten",
    image: "🍢"
  },
  {
    id: 7,
    name: "Poulette Soep",
    description: "Huisgemaakte kippensoep met verse groenten en noedels.",
    price: 8.90,
    category: "voorgerechten",
    image: "🍲"
  },
  {
    id: 8,
    name: "Veggie Poulette",
    description: "Vegetarische versie van onze special met seizoensgroenten en quinoa.",
    price: 20.50,
    category: "hoofdgerechten",
    image: "🥬"
  },
  {
    id: 9,
    name: "Kip Nuggets",
    description: "Krokante kipnuggets met zoetzure saus. Perfect voor kinderen.",
    price: 14.90,
    category: "kinderen",
    image: "🍗"
  },
  {
    id: 10,
    name: "Poulette Dessert",
    description: "Huisgemaakte tiramisu met een twist van koffie en amaretto.",
    price: 9.50,
    category: "desserts",
    image: "🍰"
  }
];

const categories = [
  { id: 'all', name: 'Alles' },
  { id: 'voorgerechten', name: 'Voorgerechten' },
  { id: 'hoofdgerechten', name: 'Hoofdgerechten' },
  { id: 'burgers', name: 'Burgers' },
  { id: 'salades', name: 'Salades' },
  { id: 'kinderen', name: 'Kinderen' },
  { id: 'desserts', name: 'Desserts' }
];

const restaurants = [
  {
    id: '1',
    name: 'Poule & Poulette Brussel Centrum',
    address: 'Grote Markt 15, 1000 Brussel',
    phone: '+32 2 123 45 67'
  },
  {
    id: '2',
    name: 'Poule & Poulette Antwerpen',
    address: 'Meir 45, 2000 Antwerpen',
    phone: '+32 3 123 45 67'
  },
  {
    id: '3',
    name: 'Poule & Poulette Gent',
    address: 'Korenmarkt 12, 9000 Gent',
    phone: '+32 9 123 45 67'
  },
  {
    id: '4',
    name: 'Poule & Poulette Leuven',
    address: 'Oude Markt 8, 3000 Leuven',
    phone: '+32 16 123 45 67'
  },
  {
    id: '5',
    name: 'Poule & Poulette Brugge',
    address: 'Markt 20, 8000 Brugge',
    phone: '+32 50 123 45 67'
  }
];

const TakeawayPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [cart, setCart] = useState<CartItem[]>([]);
  const [selectedRestaurant] = useState(searchParams.get('restaurant') || '1');

  const filteredItems = selectedCategory === 'all' 
    ? menuItems 
    : menuItems.filter(item => item.category === selectedCategory);

  const addToCart = (item: MenuItem) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        return [...prevCart, { ...item, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (itemId: number) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeFromCart(itemId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.id === itemId ? { ...item, quantity: newQuantity } : item
      )
    );
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  const handleCheckout = () => {
    // Simulate checkout process
    alert('Bestelling geplaatst! Je ontvangt een bevestiging per e-mail.');
    setCart([]);
  };

  const selectedRestaurantData = restaurants.find(r => r.id === selectedRestaurant);

  return (
    <PageContainer>
      <HeroSection>
        <HeroTitle>Takeaway Bestellen</HeroTitle>
        <HeroSubtitle>
          Bestel online en haal je favoriete gerechten op
        </HeroSubtitle>
      </HeroSection>

      <ContentGrid>
        <MenuSection>
          <SectionTitle>
            <ShoppingCart size={24} />
            Menu ({getCartCount()} items in winkelwagen)
          </SectionTitle>

          <CategoryTabs>
            {categories.map((category) => (
              <CategoryTab
                key={category.id}
                active={selectedCategory === category.id}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.name}
              </CategoryTab>
            ))}
          </CategoryTabs>

          <MenuGrid>
            {filteredItems.map((item) => (
              <MenuItem
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
              >
                <MenuItemHeader>
                  <MenuItemTitle>{item.name}</MenuItemTitle>
                  <MenuItemPrice>€{item.price.toFixed(2)}</MenuItemPrice>
                </MenuItemHeader>
                <MenuItemDescription>{item.description}</MenuItemDescription>
                <MenuItemActions>
                  <QuantityControls>
                    <QuantityButton
                      onClick={() => addToCart(item)}
                    >
                      <Plus size={16} />
                    </QuantityButton>
                  </QuantityControls>
                </MenuItemActions>
              </MenuItem>
            ))}
          </MenuGrid>
        </MenuSection>

        <CartSection>
          <SectionTitle>
            <ShoppingCart size={24} />
            Winkelwagen
          </SectionTitle>

          {selectedRestaurantData && (
            <RestaurantInfo>
              <h4>{selectedRestaurantData.name}</h4>
              <p><MapPin size={14} /> {selectedRestaurantData.address}</p>
              <p><Phone size={14} /> {selectedRestaurantData.phone}</p>
              <p><Clock size={14} /> Bereidingstijd: 15-20 minuten</p>
            </RestaurantInfo>
          )}

          <AnimatePresence>
            {cart.length === 0 ? (
              <EmptyCart>
                <div className="icon">🛒</div>
                <p>Je winkelwagen is leeg</p>
                <p>Voeg gerechten toe om te bestellen</p>
              </EmptyCart>
            ) : (
              <>
                <CartItems>
                  {cart.map((item) => (
                    <CartItem
                      key={item.id}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                    >
                      <CartItemInfo>
                        <h4>{item.name}</h4>
                        <p>€{item.price.toFixed(2)} per stuk</p>
                      </CartItemInfo>
                      <CartItemActions>
                        <QuantityControls>
                          <QuantityButton
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          >
                            <Minus size={12} />
                          </QuantityButton>
                          <Quantity>{item.quantity}</Quantity>
                          <QuantityButton
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          >
                            <Plus size={12} />
                          </QuantityButton>
                        </QuantityControls>
                        <CartItemPrice>€{(item.price * item.quantity).toFixed(2)}</CartItemPrice>
                        <RemoveButton
                          onClick={() => removeFromCart(item.id)}
                        >
                          <Trash2 size={12} />
                        </RemoveButton>
                      </CartItemActions>
                    </CartItem>
                  ))}
                </CartItems>

                <CartTotal>
                  <TotalRow>
                    <span>Subtotaal:</span>
                    <span>€{getCartTotal().toFixed(2)}</span>
                  </TotalRow>
                  <TotalRow>
                    <span>BTW (21%):</span>
                    <span>€{(getCartTotal() * 0.21).toFixed(2)}</span>
                  </TotalRow>
                  <TotalRow className="total">
                    <span>Totaal:</span>
                    <span>€{(getCartTotal() * 1.21).toFixed(2)}</span>
                  </TotalRow>
                </CartTotal>

                <CheckoutButton onClick={handleCheckout}>
                  Bestelling Plaatsen
                </CheckoutButton>
              </>
            )}
          </AnimatePresence>
        </CartSection>
      </ContentGrid>
    </PageContainer>
  );
};

export default TakeawayPage;
